import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'redux-zero/react';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import Media from 'react-media';

import { actions } from '../../shared/store.js';
import NoDataComponent from '../../components/no-data-component/no-data.js';
import Slider from '../../components/slider/slider.js';
import SlideModel from '../../components/slider/slide.model.js';
import NotImplemented from '../../components/not-implemented/page-not-implemented.js';
import Award from '../../components/award/award.component.js';
import AwardsService from './awards.service.js';
import ScrollToTop from '../../shared/utils.js';
import Seo from '../../infrastructure/seo.js';

import './awards.scss';

class AwardsPage extends Component {
	static prefetchData = async language => AwardsService.getAwards(language);

	state = {
		hasError: false,
	};

	componentDidMount() {
		const { awards, setLoading } = this.props;

		ScrollToTop();
		if (!awards) {
			this.fetchData();
		} else {
			setLoading(false);
		}
	}

	componentDidUpdate(prevProps) {
		const { language, location } = this.props;

		ScrollToTop();
		if (
			language.value !== prevProps.language.value
			|| location.search !== prevProps.location.search
		) {
			this.fetchData();
		}
	}

	fetchData() {
		const { language, setAwards, setLoading } = this.props;

		setLoading(true);
		AwardsPage.prefetchData(language.value)
			.then((data) => {
				setLoading(false);
				setAwards(data);
			})
			.catch((error) => {
				setLoading(false);
				this.setState({
					hasError: true,
				});
			});
	}

	render() {
		const { awards, intl: { formatMessage }, addresses, phones, generalData } = this.props;
		const { hasError } = this.state;

		if (hasError) {
			return <NotImplemented title="Awards" />;
		}

		let awardsPage = null;

		if (!awards) {
			awardsPage = (
				<NoDataComponent
					title={formatMessage({ id: 'awards.no-results.title' })}
					message={formatMessage({
						id: 'awards.no-results.message',
					})}
					link="/"
					buttonLabel={formatMessage({ id: 'button.goToHome' })}
				/>
			);
		} else {
			const awardsCards = awards.awards.map(
				(award, index) => new SlideModel({
					id: index,
					children: (
						<Award
							index={index}
							award={award.value}
							totalItems={awards.awards.length}
						/>
					),
				}),
			);

			awardsPage = (
				<div className="award">
					<div className="page-intro">
						<div className=" container container-fluid">
							<div className="section intro row">
								<div className="col-xs-12  center-xs title">{awards.title}</div>
								<div className="col-xs-12 center-xs subtitle">
									{awards.description}
								</div>
							</div>
						</div>
					</div>

					<Media query="(max-width: 599px)">
						{matches => (matches ? (
							<div>
								<Slider slides={awardsCards} unity={80} margin={10} />
							</div>
						) : (
							<Awards title={awards.title} awards={awards.awards} />
						))
						}
					</Media>
				</div>
			);
		}

		return (
			<div id="awardsPage">
				<Seo
					title={formatMessage({ id: 'navigation.about.awards' })}
					description={(awards && awards.description) || formatMessage({ id: 'navigation.about.awards' })}
					microdata={`{
									"@context": "https://schema.org",
									"@graph": [
										{
											"@type": "AboutPage",
											"name": "${awards && awards.title}",
											"description": "${awards && awards.description}"
										},
										{
											"@type": "WebApplication",
											"name": "Inocrowd",
											"url": "${process.env.REACT_APP_WEBSITE_ENDPOINT}",
											"applicationCategory": "BusinessApplication",
											"operatingSystem": "Windows, Linux, OSX, iOS, Android",
											"sameAs": [
												"${process.env.REACT_APP_PLATFORM_ENDPOINT}",
												"https://www.facebook.com/InoCrowd/",
												"https://www.linkedin.com/company/inocrowd",
												"https://twitter.com/InoCrowd",
												"https://www.instagram.com/inocrowd",
												"https://www.youtube.com/channel/UC8JotJA9Txz_6NQXWuEwdvQ"
											],
											"provider": {
												"@type": "Organization",
												"name": "Inocrowd",
												${addresses.length > 0 && `
													"address":  "${addresses[0].description.replace(/\n/g, ' ')}",
												`}
												${phones.length > 0 && `
													"contactPoint": {
														"@type": "ContactPoint",
														"telephone": "${phones[0].phoneNumber}",
														"contactType": "customer service"
													},
													"telephone": "${phones[0].phoneNumber}",
												`}
												${generalData && generalData.logo ? `
													"logo": "${process.env.REACT_APP_API_ENDPOINT}${generalData.logo.path}"
												` : ''}
											}
										},
										{
											"@type": "BreadcrumbList",
											"itemListElement": [
												{
													"@type": "ListItem",
													"name": "Inocrowd",
													"position": 1,
													"item": "${process.env.REACT_APP_WEBSITE_ENDPOINT}"
												},
												{
													"@type": "ListItem",
													"name": "${formatMessage({ id: 'navigation.about.awards' })}",
													"position": 2,
													"item": "${process.env.REACT_APP_WEBSITE_ENDPOINT}/about/awards"
												}
											]
										}
									]
								}`}
				/>
				{awardsPage}
			</div>
		);
	}
}

// eslint-disable-next-line react/no-multi-comp
class Awards extends React.Component {
	constructor(props) {
		super(props);
		this.listStart = React.createRef();
		this.listEnd = React.createRef();
		this.list = React.createRef();

		this.state = {
			hideButtonPrev: true,
			hideButtonNext: true,
		};
	}

	componentDidMount() {
		this.listStartObserver = new IntersectionObserver((entries) => {
			const buttonPrev = entries[0];

			if (!buttonPrev.isIntersecting) {
				this.setState({ hideButtonPrev: false });
			} else {
				this.setState({ hideButtonPrev: true });
			}
		});

		this.listEndObserver = new IntersectionObserver((entries) => {
			const buttonNext = entries[0];

			if (!buttonNext.isIntersecting) {
				this.setState({ hideButtonNext: false });
			} else {
				this.setState({ hideButtonNext: true });
			}
		});

		this.listStartObserver.observe(this.listStart.current);
		this.listEndObserver.observe(this.listEnd.current);
	}

	componentWillUnmount() {
		this.listStartObserver.unobserve(this.listStart.current);
		this.listEndObserver.unobserve(this.listEnd.current);
	}

	handleSliderNavigationPrev = () => {
		this.list.current.scrollBy({
			left: -320,
			behavior: 'smooth',
		});
	};

	handleSliderNavigationNext = () => {
		this.list.current.scrollBy({
			left: 320,
			behavior: 'smooth',
		});
	};

	render() {
		const { hideButtonPrev, hideButtonNext } = this.state;
		const { awards = [] } = this.props;

		if (!awards) return null;

		return (
			<section className="awards-wrapper">
				<div className="slider-wrapper-outer">
					<div className="controls">
						<button
							className={classNames('prev', { hide: hideButtonPrev })}
							onClick={this.handleSliderNavigationPrev}
							type="button"
						/>

						<button
							className={classNames('next', { hide: hideButtonNext })}
							onClick={this.handleSliderNavigationNext}
							type="button"
						/>
					</div>
					<div className="slider-wrapper-inner" ref={this.list}>
						<div className="slider">
							<div
								ref={this.listStart}
								style={{ width: 0, height: 500, display: 'inline-block' }}
							/>
							{awards.map((award, index) => (
								<div
									className={classNames(
										'card',
										{ first: index === 0 },
										{ last: index === awards.length - 1 },
									)}
									key={award.value.name}
								>
									{award.value.image && (
										<div className="image-section">
											<div
												className="image"
												style={{
													backgroundImage: `url('${process.env
														.REACT_APP_API_ENDPOINT
														+ award.value.image.path}')`,
												}}
											/>
										</div>
									)}
								</div>
							))}
							<div
								ref={this.listEnd}
								style={{ width: 0, height: 500, display: 'inline-block' }}
							/>
						</div>
					</div>
				</div>
			</section>
		);
	}
}

const mapToProps = ({ language, awards, addresses, phones, generalData }) => ({
	language,
	awards,
	addresses,
	phones,
	generalData,
});
export default connect(
	mapToProps,
	actions,
)(withRouter(injectIntl(AwardsPage)));

export { AwardsPage };
